import type { ServiceWorkerMessage } from '../types/pwa';
import { env } from '../config/env';

export function registerServiceWorker() {
  if ('serviceWorker' in navigator) {
    window.addEventListener('load', async () => {
      try {
        const registration = await navigator.serviceWorker.register('/sw.js', {
          scope: '/',
        });

        // Handle updates
        registration.addEventListener('updatefound', () => {
          const newWorker = registration.installing;
          if (newWorker) {
            newWorker.addEventListener('statechange', () => {
              if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                // New content is available, show update notification
                if (confirm('New version available! Would you like to update?')) {
                  window.location.reload();
                }
              }
            });
          }
        });

        // Optional: Check for updates periodically
        setInterval(async () => {
          try {
            await registration.update();
          } catch (error) {
            console.error('Error checking for SW updates:', error);
          }
        }, 1000 * 60 * 60); // Check every hour

        console.log('Service Worker registered successfully:', registration.scope);
      } catch (error) {
        console.error('Service Worker registration failed:', error);
      }
    });

    // Handle offline/online events
    window.addEventListener('online', () => {
      console.log('Application is online');
      document.dispatchEvent(new CustomEvent('app-status', { detail: { online: true } }));
    });

    window.addEventListener('offline', () => {
      console.log('Application is offline');
      document.dispatchEvent(new CustomEvent('app-status', { detail: { online: false } }));
    });

    // Handle communication with Service Worker
    navigator.serviceWorker.addEventListener('message', (event) => {
      const message = event.data as ServiceWorkerMessage;
      if (message?.type === 'CACHE_UPDATED') {
        console.log('New content is cached and ready for use');
      }
    });
  }
}

export function unregisterServiceWorker() {
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.ready
      .then((registration) => {
        registration.unregister();
      })
      .catch((error) => {
        console.error('Service Worker unregistration failed:', error);
      });
  }
}

// Check if the app can be installed (PWA)
export function checkInstallability() {
  // Only check if the API is supported and we're in a secure context
  if (
    'getInstalledRelatedApps' in navigator &&
    typeof navigator.getInstalledRelatedApps === 'function' &&
    window.isSecureContext
  ) {
    try {
      // Bind the method to navigator to prevent 'Illegal invocation'
      const boundGetInstalledApps = navigator.getInstalledRelatedApps.bind(navigator);
      boundGetInstalledApps().then((apps) => {
        const isInstalled = apps.length > 0;
        document.dispatchEvent(
          new CustomEvent('app-installability', {
            detail: { canInstall: !isInstalled }
          })
        );
      }).catch((error) => {
        console.error('Error checking app installability:', error);
      });
    } catch (error) {
      console.error('Error accessing getInstalledRelatedApps:', error);
    }
  } else {
    console.log('getInstalledRelatedApps is not supported in this browser');
  }
}

// Request notification permission
export async function requestNotificationPermission() {
  if ('Notification' in window) {
    try {
      const permission = await Notification.requestPermission();
      return permission === 'granted';
    } catch (error) {
      console.error('Error requesting notification permission:', error);
      return false;
    }
  }
  return false;
}

// Subscribe to push notifications
export async function subscribeToPushNotifications(registration: ServiceWorkerRegistration) {
  try {
    const subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(env.VITE_VAPID_PUBLIC_KEY || '')
    });
    
    console.log('Push notification subscription:', subscription);
    return subscription;
  } catch (error) {
    console.error('Failed to subscribe to push notifications:', error);
    return null;
  }
}

// Helper function to convert VAPID key
function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}